import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { AuthShell } from "../components/AuthShell";
import { useTranslation } from "../i18n";
import { Icon, LogoMark } from "../components/icons";
import { Alert, Button, Spinner, cx } from "../components/ui";
import { ApiError, api } from "../lib/api";
import { initials } from "../lib/team";

/**
 * The consent screen, and the only page in this dashboard that exists for
 * another service.
 *
 * The authorization server renders nothing — it answers JSON. What a person
 * sees is decided here, because this is where sign-in already lives: somebody
 * arriving without a session hits `RequireAuth`, signs in against the form they
 * know, and comes back to this URL with its query string intact. Rebuilding a
 * sign-in form on the API for the sake of one flow would have meant two places
 * that take a password.
 *
 * Nothing on this page is trusted. Every parameter is echoed back to the API on
 * submit and validated there — this page cannot widen a scope or change a
 * redirect URI by editing what it displays, because it is not what decides.
 */

interface AuthorizeInfo {
  client: { id: string; name: string };
  scope: string;
  redirectUri: string;
  consentRequired: boolean;
}

/**
 * What each scope permits, in words rather than in identifiers — kept under
 * `oauth.scopes` in the auth translations — as a phrase
 * that reads inside one sentence: "Community will see your name and account
 * id. Nothing else."
 *
 * That sentence replaced two lists — what the client can read and a four-item
 * list of what it cannot — and the refusals were the half that did the work,
 * because "access your account" leaves somebody wondering whether it reaches
 * the things they would mind losing. "Nothing else." is that half kept: two
 * words that close the question instead of four lines that enumerate it. A
 * scope this page has no words for falls back to its identifier, which is
 * ugly on purpose — a new scope should arrive with its phrase.
 */
export function OAuthAuthorizePage() {
  const [params] = useSearchParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("auth");

  const [info, setInfo] = useState<AuthorizeInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const query = params.toString();

  useEffect(() => {
    let cancelled = false;

    void api<AuthorizeInfo>(`/v1/oauth/authorize?${query}`)
      .then((body) => {
        if (cancelled) return;
        setInfo(body);
        /*
         * Already agreed to this scope, so there is nothing to ask. Submitting
         * immediately is what makes a returning member's sign-in a redirect
         * they never see rather than a dialog they answer every time.
         */
        if (!body.consentRequired) void submit(body);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(
          cause instanceof ApiError
            ? cause.message
            : t("oauth.readFailed"),
        );
      });

    return () => {
      cancelled = true;
    };
    // Deliberately keyed on the query alone: this page is about one request,
    // and re-running when anything else changes would re-mint a code.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  async function submit(current: AuthorizeInfo) {
    setSubmitting(true);
    setError(null);
    try {
      const { redirectTo } = await api<{ redirectTo: string }>("/v1/oauth/authorize", {
        method: "POST",
        body: {
          client_id: params.get("client_id"),
          redirect_uri: params.get("redirect_uri"),
          scope: current.scope,
          state: params.get("state"),
          code_challenge: params.get("code_challenge"),
          code_challenge_method: params.get("code_challenge_method"),
        },
      });

      /*
       * A full navigation, not a router push. The destination is another
       * origin, and it is the browser's address bar that has to move — the code
       * is being delivered to a server, not to this app.
       */
      window.location.replace(redirectTo);
    } catch (cause: unknown) {
      setSubmitting(false);
      setError(
        cause instanceof ApiError ? cause.message : t("oauth.completeFailed"),
      );
    }
  }

  /**
   * Refusing, reported to the client rather than left as a dead end.
   *
   * `access_denied` is the answer OAuth defines for exactly this, and sending it
   * means the community can say "you cancelled" instead of "something went
   * wrong". The redirect URI comes from the API's validated response, never from
   * the query string, so Cancel cannot be turned into an open redirect by
   * whoever composed the link.
   */
  function deny() {
    if (!info) return;
    const target = new URL(info.redirectUri);
    target.searchParams.set("error", "access_denied");
    const state = params.get("state");
    if (state) target.searchParams.set("state", state);
    window.location.replace(target.href);
  }

  /**
   * Signing out without losing the request.
   *
   * Somebody who reaches this page on the wrong account is otherwise stuck: the
   * way out is to sign out, and signing out normally lands on `/login` with
   * nothing to come back to. The current URL is handed to the login page as
   * `from`, which is exactly what `RequireAuth` does — so they return here with
   * the request intact rather than to an empty dashboard.
   */
  async function switchAccount() {
    await logout();
    void navigate("/login", { replace: true, state: { from: `/oauth/authorize?${query}` } });
  }

  if (error) {
    return (
      <AuthShell title={t("oauth.failedTitle")}>
        <Alert tone="error">{error}</Alert>
        <p className="mt-6 text-sm leading-6 text-fg-muted">
          {t("oauth.nothingShared")}
        </p>
      </AuthShell>
    );
  }

  /*
   * One screen for "still loading" and for "already authorized, submitting",
   * and they are deliberately the same. Somebody who has agreed before should
   * see a moment of nothing and then be somewhere else — flashing the full
   * dialog and then submitting it for them reads as the page acting on its own.
   */
  if (!info || !info.consentRequired || submitting) {
    /*
     * The same handshake the consent screen draws, so the loading screen and
     * the screen it becomes are one picture rather than two — and for a
     * returning member, who never sees the dialog, this is the whole visit.
     * The client's tile is a placeholder until the request has been read,
     * because its name is one of the things being checked; the connector
     * pulses while something is in flight, and stops when nothing is.
     */
    const client = info?.client.name;
    const leaving = submitting || (info !== null && !info.consentRequired);
    return (
      <AuthShell title={t("oauth.oneMoment")} emblem={<Handshake client={client} busy />}>
        <div
          role="status"
          aria-live="polite"
          className="flex items-center justify-center gap-2.5 text-sm text-fg-muted"
        >
          <Spinner className="size-4" />
          <span>{leaving && client ? t("oauth.redirecting", { client }) : t("oauth.checking")}</span>
        </div>
      </AuthShell>
    );
  }

  const scopes: Record<string, string | undefined> = t("oauth.scopes", { returnObjects: true });
  const grants = info.scope
    .split(/\s+/)
    .filter(Boolean)
    .map((scope) => scopes[scope] ?? scope)
    .join(t("oauth.grantSeparator"));
  // The host, not the whole URI. A path and a query string on a consent screen
  // is noise nobody reads; the hostname is the part somebody can actually check.
  const destination = new URL(info.redirectUri).host;

  return (
    <AuthShell
      title={t("oauth.title", { client: info.client.name })}
      /* The whole disclosure, in the shell's subtitle slot: what is shared and
         that nothing more is. It is the one sentence on this screen that is not
         a name, a hostname or a button. */
      subtitle={t("oauth.subtitle", { client: info.client.name, grants })}
      emblem={<Handshake client={info.client.name} />}
    >
      <div className="space-y-5">
        {/*
          The account, drawn as the decision rather than stated as a fact.

          "Signed in as" was a line of muted text under two lists, and the wrong
          account is the mistake people actually make here — somebody with a
          work login and a personal one, arriving from a community they read
          under the other. So the account is the largest object on the screen:
          a monogram, the name, the address, and the way out beside them. Same
          tile the profile and team pages draw, so a face somebody has seen in
          the console is the face they see here.
        */}
        {user && (
          <div className="flex items-center gap-3 rounded-lg border border-line bg-surface p-3.5">
            <span
              aria-hidden="true"
              className="grid size-10 shrink-0 place-items-center rounded-md bg-raised text-[13px] font-medium text-fg-muted"
            >
              {initials(user.name)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="micro">{t("oauth.continueAs")}</p>
              <p className="mt-0.5 truncate text-sm font-medium text-fg">{user.name}</p>
              <p className="truncate text-[13px] text-fg-muted">{user.email}</p>
            </div>
            {/*
              The way out for somebody on the wrong account. Without it this page
              is a dead end for exactly the person most likely to be reading it
              carefully.
            */}
            <button
              type="button"
              onClick={() => void switchAccount()}
              className="shrink-0 text-[13px] text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
            >
              {t("oauth.switch")}
            </button>
          </div>
        )}

        {/*
          Where Allow sends you, and it is the anti-phishing affordance on this
          screen — the reason it is set in mono: a hostname is a string somebody
          compares character by character, and a proportional face makes that
          harder for exactly the substitutions that matter. A quiet row rather
          than a card: it is there to be checked, not to compete with the account.
        */}
        <div className="flex items-center justify-between gap-3 border-t border-line pt-4 text-[13px]">
          <span className="text-fg-subtle">{t("oauth.returnsTo")}</span>
          <span className="truncate font-mono text-fg-muted" title={destination}>
            {destination}
          </span>
        </div>

        {/*
          Allow first and wider, because it is the action the page exists for —
          but Cancel is a peer beside it rather than a link tucked underneath.
          Refusing has to be as findable as agreeing, which is the rule the
          consent banner on the marketing site already lives under. `xl` on
          both, because this is the auth surface and its controls are 44px.
        */}
        <div className="flex gap-3 pt-1">
          <Button
            size="xl"
            className="flex-1"
            onClick={() => void submit(info)}
            loading={submitting}
          >
            {t("oauth.allow")}
          </Button>
          <Button size="xl" variant="secondary" onClick={deny} disabled={submitting}>
            {t("oauth.cancel")}
          </Button>
        </div>
      </div>
    </AuthShell>
  );
}

/**
 * The two parties, drawn as a pair: the client on the left as a monogram, the
 * platform on the right as its mark, a dashed line between them with the link
 * icon at its middle. It replaces the shell's Maxlayer lockup on this one
 * screen, because the question here is not "where am I" but "who is asking
 * whom" — and that is a picture before it is a sentence.
 *
 * A monogram rather than a logo for the client, because the API hands over a
 * name and an id and nothing else; an image URL would be one more thing a
 * registered client could point at something it should not. Two letters from
 * the name are enough to recognise and impossible to abuse.
 */
function Handshake({ client, busy = false }: { client?: string; busy?: boolean }) {
  const { t } = useTranslation("auth");
  return (
    <div
      className="flex items-center"
      aria-label={client ? t("oauth.pair", { client }) : t("oauth.checkingRequest")}
      role="img"
    >
      {/* No name yet: an empty tile that breathes, in the raised tone rather
          than the surface one, so it reads as "not filled in" and not as a
          client with a blank name. */}
      {client ? (
        <span
          aria-hidden="true"
          className="grid size-12 shrink-0 place-items-center rounded-lg border border-line bg-surface text-sm font-medium tracking-[-0.01em] text-fg"
        >
          {initials(client)}
        </span>
      ) : (
        <span
          aria-hidden="true"
          className="size-12 shrink-0 animate-pulse rounded-lg border border-line bg-raised motion-reduce:animate-none"
        />
      )}
      <span className="relative mx-1 w-14 shrink-0" aria-hidden="true">
        <span className="block border-t border-dashed border-line-strong" />
        <span
          className={cx(
            "absolute top-1/2 left-1/2 grid size-6 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-line bg-canvas text-accent",
            busy && "animate-pulse motion-reduce:animate-none",
          )}
        >
          <Icon name="link" className="size-3" />
        </span>
      </span>
      <span
        aria-hidden="true"
        className="grid size-12 shrink-0 place-items-center rounded-lg border border-line bg-surface"
      >
        <LogoMark className="size-6" />
      </span>
    </div>
  );
}
